import * as Dialog from '@radix-ui/react-dialog';
import Card from 'components/card';
import { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { usersService } from 'views/services/usersService';
import { adminService } from 'views/services/adminService';
import { Button } from 'components/button';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { IUserCurrencyBalance, TBalanceType } from 'views/types/balances';
import { toast } from 'App';

interface IProps {
  children: React.ReactNode;
  userId: string;
  item: IUserCurrencyBalance;
  balanceType: TBalanceType;
  onSaved(): void;
}

const AMOUNT_REGEX = /^\d+(\.\d+)?$/;

export const EditBalanceDialog: React.FC<IProps> = ({
  children,
  userId,
  item,
  balanceType,
  onSaved,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { canUpdate } = usePermissions();

  const isBlocked = balanceType === 'blocked';
  const currentBalance = isBlocked ? item.blockedBalance : item.balance;

  const normalized = value.trim().replace(',', '.');
  const fraction = normalized.split('.')[1];
  const isFormatValid = AMOUNT_REGEX.test(normalized);
  const isPrecisionValid = !fraction || fraction.length <= item.decimals;
  const isChanged = normalized !== currentBalance;

  // Blocked edits MOVE funds: raising blocked debits available, lowering it
  // credits available back — so the maximum is current blocked + available.
  // Client-side check only; the server enforces this exactly.
  const maxBlocked = Number(item.blockedBalance) + Number(item.balance);
  const isWithinAvailable =
    !isBlocked || !isFormatValid || Number(normalized) <= maxBlocked;
  const projectedAvailable = isBlocked
    ? Number(item.balance) - (Number(normalized) - Number(item.blockedBalance))
    : null;

  const canSave =
    isFormatValid &&
    isPrecisionValid &&
    isChanged &&
    isWithinAvailable &&
    !isLoading;

  const validationHint = !value.trim()
    ? ''
    : !isFormatValid
      ? 'Enter a non-negative number, e.g. 123.45'
      : !isPrecisionValid
        ? `Max ${item.decimals} decimal places for ${item.currency}`
        : !isWithinAvailable
          ? `Cannot block more than available (max ${maxBlocked} ${item.currency})`
          : '';

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setValue(currentBalance);
      setErrorMessage('');
    }
  };

  const handleSave = async () => {
    if (!canUpdate(PERMISSION_RESOURCES.CLIENT_BALANCE_EDIT)) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to edit client balances',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage('');
      const result = await usersService.updateUserBalance(
        userId,
        item.currency,
        balanceType,
        normalized
      );
      toast({
        title: `${balanceType === 'blocked' ? 'Blocked' : 'Available'} balance updated`,
        description:
          `${result.currency}: ${result.oldBalance} → ${result.newBalance}` +
          (result.newAvailableBalance != null
            ? ` (available now ${result.newAvailableBalance})`
            : ''),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onSaved();
      setIsOpen(false);
    } catch (error) {
      console.error(error);
      setErrorMessage(adminService.getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
        <Dialog.Content
          onInteractOutside={e => {
            if ((e.target as HTMLElement).closest('.chakra-toast')) {
              e.preventDefault();
            }
          }}
          className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] max-h-[85vh] w-[90vw] max-w-[480px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white lg-max:px-2 p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none dark:bg-gray-800 dark:text-white"
        >
          <Dialog.Title className="flex justify-center items-center relative text-center text-mauve12 mb-6 text-[17px] font-medium">
            Edit {item.currency} {isBlocked ? 'admin blocked' : balanceType}{' '}
            balance
          </Dialog.Title>
          <Card extra={'w-full sm:overflow-auto lg-max:px-1 px-6 pb-6'}>
            <div className="flex flex-col gap-4 py-4">
              <p>
                Current {isBlocked ? 'admin blocked' : balanceType} balance:{' '}
                <span className="font-semibold">
                  {currentBalance} {item.currency}
                </span>
              </p>
              {isBlocked && (
                <p className="text-sm dark:text-white/60 text-[black]/60">
                  Blocking moves funds out of Available (available:{' '}
                  {item.balance} {item.currency}); unblocking returns them.
                </p>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-sm">New balance</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  disabled={isLoading}
                  className="rounded border border-gray-300 bg-white px-3 py-2 outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-gray-700"
                />
              </label>
              {validationHint && (
                <span className="text-sm text-red-600">{validationHint}</span>
              )}
              {canSave && (
                <p className="text-sm">
                  {item.currency} ({isBlocked ? 'admin blocked' : balanceType}
                  ): {currentBalance} →{' '}
                  <span className="font-semibold">{normalized}</span>
                  {projectedAvailable != null && (
                    <span className="block dark:text-white/60 text-[black]/60">
                      Available after save: {projectedAvailable} {item.currency}
                    </span>
                  )}
                </p>
              )}
              <Button
                onClick={handleSave}
                disabled={!canSave}
                className="justify-center"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
              {errorMessage && (
                <span className="text-red-600">{errorMessage}</span>
              )}
            </div>
          </Card>
          <Dialog.Close asChild>
            <button
              type="button"
              className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
              aria-label="Close"
            >
              <MdClose />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
